/**
 * Server settings: create and edit channels (with their permissions, see
 * Overwrites.tsx) and roles (Roles.tsx), plus the moderation tools. Every
 * control is permission-gated on the client the way the server decides
 * (the protocol's calculators and hierarchy); the server checks again
 * (rule 5).
 */
import {
  ChannelType,
  isVoiceChannelType,
  overwritesInSync,
  Permissions,
  type Channel,
  type ModifyChannelRequest,
  type Overwrite,
  type Snowflake,
} from '@neosocial/protocol';
import { useMemo, useRef, useState } from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { useData } from '../store/data';
import { useDataShallow } from '../store/data';
import {
  basePermissions,
  can,
  categoriesInOrder,
  channelIcon,
  channelPermissions,
  useActiveGuildId,
} from '../store/selectors';
import { useChannelTree } from '../store/selectors';
import { useUi, type AdminState } from '../store/ui';
import { Btn, Icon, Seg, Select, type IconName } from '../ui/kit';
import { serverTools } from '../mod/perms';
import { AuditTab, BansTab, DeletedTab, InvitesTab, OwnerTab } from './ServerTools';
import { ChannelReorder } from './ChannelReorder';
import { channelPositions, useDragOrder } from './reorder';
import { confirmDialog, toast } from '../ui/overlay';
import { useTopOnChange } from '../ui/scroll';
import { SetLayer, SetNav, SplitBack, useSetStep, useSplitCompact } from '../settings/Layer';
import { reportError } from '../hub/social';
import { runAction, withStepUpRetry } from '../mod/actions';
import { DESTRUCTIVE, useAdminPerms } from './adminPerms';
import {
  channelSig,
  overwriteChanges,
  rebaseChannel,
  sameChannel,
  type ChannelFields,
} from './drafts';
import { ChannelPermissions } from './Overwrites';
import { OverviewTab } from './Overview';
import { RolesTab } from './Roles';
import { holdForUnsaved, useUnsaved } from './unsaved';
import { canSeeVoiceMutes, ChannelVoiceMutes } from './VoiceMutes';
import { canSeeEmojiTab, EmojiTab } from './EmojiTab';
import { AutoModTab, SlowmodeField } from './AutoMod';
import { useFlag } from '../store/config';

export { useAdminPerms };

/* ---------------- channels ---------------- */

interface ChannelDraft extends ChannelFields {
  /** The channel's type: a new one picks 0, 2 or 4; an existing one keeps its own (13: stage). */
  type: number;
  password: string;
  clearPassword: boolean;
}

function draftOf(
  c: Channel | undefined,
  newType: 0 | 2 | 4,
  parentId: Snowflake | null,
  parentOverwrites: Overwrite[] = [],
): ChannelDraft {
  return {
    name: c?.name ?? '',
    type: c?.type ?? newType,
    parent_id: c ? (c.parent_id ?? null) : parentId,
    topic: c?.topic ?? '',
    user_limit: c?.user_limit ?? 0,
    bitrate: c?.bitrate ?? 64000,
    rate_limit_per_user: c?.rate_limit_per_user ?? 0,
    password: '',
    clearPassword: false,
    // A new channel in a category starts synced with it (Discord).
    overwrites: c?.permission_overwrites ?? (newType === 4 ? [] : parentOverwrites),
  };
}

const fieldsOf = (d: ChannelDraft): ChannelFields => ({
  name: d.name,
  parent_id: d.parent_id,
  topic: d.topic,
  user_limit: d.user_limit,
  bitrate: d.bitrate,
  rate_limit_per_user: d.rate_limit_per_user ?? 0,
  overwrites: d.overwrites,
});

/** The channel editor's state: as the edit began (`base`), the draft, the live channel last seen. */
interface ChannelEdit {
  base: ChannelDraft;
  draft: ChannelDraft;
  seen: string;
  elsewhere: boolean;
}

const wire = (o: Overwrite) => ({
  id: o.id,
  type: o.type,
  allow: o.allow,
  deny: o.deny,
  nx_allow: o.nx_allow,
  nx_deny: o.nx_deny,
});

function ChannelEditor({
  id,
  newType,
  parentId,
  onDone,
}: {
  id: Snowflake | 'new';
  newType: 0 | 2 | 4;
  parentId: Snowflake | null;
  onDone(id?: Snowflake): void;
}) {
  const { t } = useT();
  const ap = useAdminPerms();
  const channel = useData((d) => (id === 'new' ? undefined : d.channels[id]));
  const [ed, setEd] = useState<ChannelEdit>(() => {
    const d = draftOf(
      channel,
      newType,
      parentId,
      parentId ? (useData.getState().channels[parentId]?.permission_overwrites ?? []) : [],
    );
    return { base: d, draft: d, seen: channel ? channelSig(fieldsOf(d)) : '', elsewhere: false };
  });
  // The live channel changed (after a save here, or by someone else): the
  // draft follows it, keeping only what was changed here (drafts.ts), so a
  // Save never undoes another admin's overwrite or topic.
  const live = channel ? fieldsOf(draftOf(channel, newType, null)) : null;
  const liveSig = live ? channelSig(live) : '';
  if (live && liveSig !== ed.seen) {
    const edited = !sameChannel(fieldsOf(ed.draft), fieldsOf(ed.base));
    setEd(
      sameChannel(live, fieldsOf(ed.base))
        ? { ...ed, seen: liveSig }
        : {
            base: { ...ed.base, ...live },
            draft: { ...ed.draft, ...rebaseChannel(fieldsOf(ed.draft), fieldsOf(ed.base), live) },
            seen: liveSig,
            elsewhere: ed.elsewhere || (edited && !sameChannel(live, fieldsOf(ed.draft))),
          },
    );
  }
  const draft = ed.draft;
  const parent = useData((d) => (draft.parent_id ? d.channels[draft.parent_id] : undefined));
  // Your permissions as the server computes them: in the channel; for a new
  // one, in the category it goes in (server-wide without one).
  const chPerms = useDataShallow((d) =>
    channel ? channelPermissions(d, channel) : parent ? channelPermissions(d, parent) : ap.base,
  );
  const canEditOw = ap.isOwner || can(chPerms, Permissions.MANAGE_ROLES);
  // Manage channels where it applies (a voice channel you can't connect to drops it, Discord).
  const canManageHere = ap.isOwner || can(chPerms, Permissions.MANAGE_CHANNELS);
  const noConnect =
    !!channel && isVoiceChannelType(channel.type) && !can(chPerms, Permissions.CONNECT);
  // Categories a channel may go in: those you manage channels in (and the one it is in now).
  // in the sidebar's order
  const cats = useDataShallow((d) =>
    categoriesInOrder(d, ap.gid).filter(
      (c) =>
        c.id === (channel ? channel.parent_id : parentId) ||
        can(channelPermissions(d, c), Permissions.MANAGE_CHANNELS),
    ),
  );
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const isNew = id === 'new';
  // a stage channel is a voice channel too: its voice fields and voice
  // permissions, as the server treats it (it showed a text channel's)
  const voice = isVoiceChannelType(draft.type);
  const cat = draft.type === ChannelType.GUILD_CATEGORY;
  // slowmode (2026-10-29): text channels, while the console has it on
  const slowmodeOn = useFlag('slowmode');
  const slowmodeHere = slowmodeOn && draft.type === ChannelType.GUILD_TEXT;
  const patch = (p: Partial<ChannelDraft>) => setEd((e) => ({ ...e, draft: { ...e.draft, ...p } }));
  const owsOf = (pid: Snowflake | null) =>
    pid ? (useData.getState().channels[pid]?.permission_overwrites ?? []) : [];
  const fieldsOff = !isNew && !canManageHere;
  const dirty =
    !sameChannel(fieldsOf(draft), fieldsOf(ed.base)) || !!draft.password || draft.clearPassword;
  useUnsaved('channel', dirty);
  const reset = () => setEd((e) => ({ ...e, draft: e.base, elsewhere: false }));
  // "Muted in this channel" (2026-10-28): Mute or Deafen Members here
  const voiceMod = useData(
    (d) => !!channel && isVoiceChannelType(channel.type) && canSeeVoiceMutes(d, channel),
  );

  /** What a PATCH changes: only the fields changed here (nothing changed, no PATCH). */
  const fieldChanges = (): ModifyChannelRequest => {
    const b = ed.base;
    return {
      ...(draft.name.trim() !== b.name.trim() ? { name: draft.name.trim() } : {}),
      ...(!cat && draft.parent_id !== b.parent_id ? { parent_id: draft.parent_id } : {}),
      ...(draft.type === ChannelType.GUILD_TEXT && draft.topic.trim() !== b.topic.trim()
        ? { topic: draft.topic.trim() || null }
        : {}),
      ...(slowmodeHere && (draft.rate_limit_per_user ?? 0) !== (b.rate_limit_per_user ?? 0)
        ? { rate_limit_per_user: draft.rate_limit_per_user ?? 0 }
        : {}),
      ...(voice && draft.user_limit !== b.user_limit ? { user_limit: draft.user_limit } : {}),
      ...(voice && draft.bitrate !== b.bitrate ? { bitrate: draft.bitrate } : {}),
      ...(voice && draft.password
        ? { nx_password: draft.password }
        : voice && draft.clearPassword
          ? { nx_password: null }
          : {}),
    };
  };

  const save = async () => {
    if (!ap.gid || (!isNew && !channel)) return;
    const gid = ap.gid;
    setBusy(true);
    try {
      const common = {
        name: draft.name.trim(),
        ...(cat ? {} : { parent_id: draft.parent_id }),
        ...(draft.type === ChannelType.GUILD_TEXT ? { topic: draft.topic.trim() || null } : {}),
        ...(slowmodeHere && draft.rate_limit_per_user
          ? { rate_limit_per_user: draft.rate_limit_per_user }
          : {}),
        ...(voice
          ? {
              user_limit: draft.user_limit,
              bitrate: draft.bitrate,
              ...(draft.password
                ? { nx_password: draft.password }
                : draft.clearPassword
                  ? { nx_password: null }
                  : {}),
            }
          : {}),
      };
      let saved: Channel;
      if (isNew) {
        // In a category the server starts the channel synced with it (Discord),
        // so its overwrites go only when they differ from the category's.
        const synced =
          !cat && !!draft.parent_id && overwritesInSync(draft.overwrites, owsOf(draft.parent_id));
        const send = !cat && draft.parent_id ? !synced : draft.overwrites.length > 0;
        saved = await withStepUpRetry(
          () =>
            api.guilds.createChannel(gid, {
              ...common,
              // a new channel is one of the three the type picker offers
              type: draft.type as 0 | 2 | 4,
              ...(send ? { permission_overwrites: draft.overwrites.map(wire) } : {}),
            }),
          DESTRUCTIVE,
        );
      } else {
        saved = channel!;
        const fields = fieldChanges();
        if (canManageHere && Object.keys(fields).length)
          saved = await api.channels.modify(id, fields);
        if (canEditOw) {
          // Only the targets changed or removed here (`base` follows the live
          // channel): an overwrite another admin added meanwhile stays.
          const { put, remove } = overwriteChanges(draft.overwrites, ed.base.overwrites);
          for (const o of put) {
            const { id: _target, ...body } = wire(o);
            await withStepUpRetry(() => api.channels.putOverwrite(id, o.id, body), DESTRUCTIVE);
          }
          for (const target of remove) await api.channels.deleteOverwrite(id, target);
        }
        // Saved: this is the new starting point.
        const done = { ...draft, password: '', clearPassword: false };
        setEd((e) => ({ ...e, base: done, draft: done, elsewhere: false }));
      }
      toast(t('ns.saved'), { icon: 'check', tone: 'ok' });
      onDone(saved.id);
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  };

  /** Sync now: a saved channel takes its category's permissions at once. */
  const sync = async (categoryOverwrites: Overwrite[]) => {
    const parent = draft.parent_id ? useData.getState().channels[draft.parent_id] : undefined;
    if (isNew || !channel) {
      patch({ overwrites: categoryOverwrites.map((o) => ({ ...o })) });
      return;
    }
    setSyncing(true);
    const r = await runAction(
      () =>
        withStepUpRetry(
          () =>
            api.channels.modify(channel.id, {
              permission_overwrites: categoryOverwrites.map(wire),
            }),
          DESTRUCTIVE,
        ),
      {},
    );
    setSyncing(false);
    if (r.ok) {
      // applied at once: the saved state too
      const ows = r.value.permission_overwrites ?? [];
      setEd((e) => ({
        ...e,
        base: { ...e.base, overwrites: ows },
        draft: { ...e.draft, overwrites: ows },
      }));
      toast(t('ow.syncDone', { category: parent?.name ?? '' }), { icon: 'check', tone: 'ok' });
    }
  };

  const remove = async () => {
    if (isNew || !channel) return;
    const ok = await confirmDialog({
      title: t('ns.admin.deleteChannel', { name: channel.name ?? '' }),
      body: t('ns.admin.deleteChannelBody'),
      confirmLabel: t('ns.admin.delete'),
      danger: true,
      icon: 'trash',
    });
    if (!ok) return;
    const r = await runAction(() => api.channels.delete(channel.id), {
      challenge: { reason: 'destructive', level: 'second_factor' },
    });
    if (r.ok) onDone();
  };

  // a channel moderator (Mute / Deafen Members here, nothing to manage):
  // the channel's settings are its "Muted in this channel" list
  if (!isNew && channel && voiceMod && !canManageHere && !canEditOw) {
    return (
      <div className="ns-adm-ed" data-voice-mod-only="">
        <h3 dir="auto">{channel.name}</h3>
        <ChannelVoiceMutes channel={channel} />
      </div>
    );
  }

  return (
    <div className="ns-adm-ed">
      <h3>
        {isNew
          ? t(cat ? 'ns.admin.newCategory' : 'ns.admin.newChannel')
          : t('ns.admin.editChannel')}
      </h3>
      {ed.elsewhere && dirty ? (
        <p className="ns-note warn" data-elsewhere="true">
          <Icon name="alert" /> {t('ns.admin.changedElsewhere')}
        </p>
      ) : null}
      {fieldsOff ? (
        <p className="ns-note warn" data-no-manage="true">
          <Icon name="lock" /> {t(noConnect ? 'ns.admin.chNoManageVoice' : 'ns.admin.chNoManage')}
        </p>
      ) : isNew && !cat && draft.parent_id && !canManageHere ? (
        <p className="ns-note warn" data-no-manage="true">
          <Icon name="lock" /> {t('ns.admin.catNoManage')}
        </p>
      ) : null}
      {isNew ? (
        <div className="ns-field">
          <span className="ns-label">{t('ns.admin.type')}</span>
          <Seg<'0' | '2' | '4'>
            label={t('ns.admin.type')}
            value={String(draft.type) as '0' | '2' | '4'}
            onChange={(v) => {
              const type = Number(v) as 0 | 2 | 4;
              const p = draft.parent_id ? useData.getState().channels[draft.parent_id] : undefined;
              patch({
                type,
                ...(type === 4
                  ? { parent_id: null, overwrites: [] }
                  : { overwrites: p?.permission_overwrites ?? draft.overwrites }),
              });
            }}
            options={[
              { value: '0', label: t('ns.admin.text'), icon: 'hash' },
              { value: '2', label: t('ns.admin.voice'), icon: 'volume' },
              { value: '4', label: t('ns.admin.category'), icon: 'list' },
            ]}
          />
        </div>
      ) : null}
      <label className="ns-field">
        <span className="ns-label">{t('ns.admin.name')}</span>
        <input
          className="nx-input"
          dir="auto"
          value={draft.name}
          maxLength={100}
          disabled={fieldsOff}
          onChange={(e) => patch({ name: e.target.value })}
          data-autofocus
        />
      </label>
      {!cat ? (
        <label className="ns-field">
          <span className="ns-label">{t('ns.admin.category')}</span>
          <Select
            className="nx-input ns-select"
            value={draft.parent_id ?? ''}
            disabled={fieldsOff}
            onChange={(e) => {
              const next = e.target.value || null;
              // A new channel that follows its category follows the new one.
              const follow = isNew && overwritesInSync(draft.overwrites, owsOf(draft.parent_id));
              patch({
                parent_id: next,
                ...(follow ? { overwrites: owsOf(next).map((o) => ({ ...o })) } : {}),
              });
            }}
          >
            <option value="">{t('ns.admin.noCategory')}</option>
            {cats.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </label>
      ) : null}
      {draft.type === ChannelType.GUILD_TEXT ? (
        <label className="ns-field">
          <span className="ns-label">{t('ns.admin.topic')}</span>
          <textarea
            className="nx-textarea"
            dir="auto"
            rows={2}
            maxLength={1024}
            value={draft.topic}
            disabled={fieldsOff}
            onChange={(e) => patch({ topic: e.target.value })}
          />
        </label>
      ) : null}
      {slowmodeHere ? (
        <SlowmodeField
          value={draft.rate_limit_per_user ?? 0}
          disabled={fieldsOff}
          onChange={(rate_limit_per_user) => patch({ rate_limit_per_user })}
        />
      ) : null}
      {voice ? (
        <div className="ns-grid2">
          <label className="ns-field">
            <span className="ns-label">{t('ns.admin.userLimit')}</span>
            <input
              className="nx-input"
              type="number"
              min={0}
              max={99}
              value={draft.user_limit}
              disabled={fieldsOff}
              onChange={(e) =>
                patch({ user_limit: Math.max(0, Math.min(99, Number(e.target.value) || 0)) })
              }
            />
            <span className="ns-help">{t('ns.admin.userLimitHelp')}</span>
          </label>
          <label className="ns-field">
            <span className="ns-label">{t('ns.admin.bitrate')}</span>
            <input
              className="nx-input"
              type="number"
              min={8}
              max={128}
              value={Math.round(draft.bitrate / 1000)}
              disabled={fieldsOff}
              onChange={(e) =>
                patch({ bitrate: Math.max(8, Math.min(128, Number(e.target.value) || 64)) * 1000 })
              }
            />
          </label>
          {/* no "Talk power needed": talk power is not a Hub setting (hub-only decision, SA-B10) */}
          <label className="ns-field">
            <span className="ns-label">{t('ns.admin.password')}</span>
            <input
              className="nx-input"
              type="password"
              autoComplete="new-password"
              placeholder={
                channel?.nx_has_password ? t('ns.admin.passwordKeep') : t('ns.admin.passwordNone')
              }
              value={draft.password}
              disabled={fieldsOff}
              onChange={(e) => patch({ password: e.target.value, clearPassword: false })}
            />
            {channel?.nx_has_password ? (
              <label className="ns-check">
                <input
                  type="checkbox"
                  checked={draft.clearPassword}
                  disabled={fieldsOff}
                  onChange={(e) => patch({ clearPassword: e.target.checked, password: '' })}
                />
                <span>{t('ns.admin.passwordClear')}</span>
              </label>
            ) : null}
          </label>
        </div>
      ) : null}
      {ap.gid ? (
        <ChannelPermissions
          guildId={ap.gid}
          channel={channel}
          kind={cat ? 'category' : voice ? 'voice' : 'text'}
          parentId={cat ? null : draft.parent_id}
          value={draft.overwrites}
          onChange={(overwrites) => patch({ overwrites })}
          onSync={(o) => void sync(o)}
          syncing={syncing}
          canSync={isNew || canManageHere}
        />
      ) : null}
      {!isNew && channel && voiceMod ? <ChannelVoiceMutes channel={channel} /> : null}
      {/* a sticky footer: Save is in reach however long the permissions are (I2) */}
      <div className="ns-row-btns end ns-adm-foot">
        {!isNew ? (
          <Btn
            kind="danger"
            size="sm"
            icon="trash"
            disabled={!canManageHere}
            onClick={() => void remove()}
          >
            {t('ns.admin.delete')}
          </Btn>
        ) : null}
        <span className="ns-fill" />
        {dirty ? (
          <>
            <span className="ns-muted sm" data-unsaved="true">
              {t('roles.unsaved')}
            </span>
            <Btn kind="ghost" size="sm" onClick={reset} disabled={busy}>
              {t('roles.reset')}
            </Btn>
          </>
        ) : null}
        <Btn
          kind="primary"
          size="sm"
          disabled={
            !draft.name.trim() || busy || (isNew ? !canManageHere : fieldsOff && !canEditOw)
          }
          busy={busy}
          onClick={() => void save()}
        >
          {isNew ? t('ns.admin.create') : t('c.save')}
        </Btn>
      </div>
    </div>
  );
}

/** Voice channels where you hold Mute or Deafen Members (their settings show "Muted in this channel"). */
function useVoiceModChannels(gid: Snowflake | null): Snowflake[] {
  return useDataShallow((d) =>
    Object.values(d.channels)
      .filter((c) => c.guild_id === gid && isVoiceChannelType(c.type) && canSeeVoiceMutes(d, c))
      .map((c) => c.id),
  );
}

function ChannelsTab({ sel, onSel }: { sel: AdminState; onSel(s: AdminState): void }) {
  const { t } = useT();
  const gid = useActiveGuildId();
  const tree = useChannelTree(gid);
  const canManage = useData((d) => can(basePermissions(d, gid), Permissions.MANAGE_CHANNELS));
  // without Manage Channels: only the voice channels you moderate (their
  // "Muted in this channel" list, 2026-10-28)
  const modIds = useVoiceModChannels(gid);
  const items = useMemo(
    () =>
      tree
        .flatMap((n) => [...(n.category ? [n.category] : []), ...n.channels])
        .filter((c) => canManage || modIds.includes(c.id)),
    [tree, canManage, modIds],
  );
  const ids = useMemo(() => items.map((c) => c.id), [items]);
  const groupOf = (id: Snowflake) => {
    const c = items.find((x) => x.id === id);
    return c?.type === ChannelType.GUILD_CATEGORY ? 'cat' : 'in:' + (c?.parent_id ?? '');
  };
  const dragProps = useDragOrder(
    ids,
    (next) => {
      if (!gid) return;
      // positions among the moved item's siblings only
      const g = groupOf(next.find((id, i) => id !== ids[i]) ?? '');
      const siblings = next.filter((id) => groupOf(id) === g);
      const cur: Record<string, number | undefined> = {};
      for (const c of items) cur[c.id] = c.position;
      const body = channelPositions(siblings, cur);
      if (!body.length) return;
      void runAction(() => withStepUpRetry(() => api.um.channelPositions(gid, body))).then(
        (r) => r.ok && toast(t('um.drag.saved'), { icon: 'check', tone: 'ok' }),
      );
    },
    () => canManage,
    groupOf,
  );
  // another channel opens at the top of its editor, not where the last one was scrolled
  const editorKey = (sel.id ?? '') + (sel.newType ?? '') + (sel.parentId ?? '');
  const main = useRef<HTMLDivElement>(null);
  useTopOnChange(main, editorKey);
  // Reorder mode: Move up / down / to a category with buttons (touch screens have no drag)
  const [reorder, setReorder] = useState(false);
  // phones and portrait tablets: the list, or the editor pushed over it (Back: the list)
  const compact = useSplitCompact();
  useSetStep(compact && !!sel.id, () => holdForUnsaved() || onSel({ tab: 'channels' }));
  return (
    <div className="ns-adm-split" data-show={compact ? (sel.id ? 'editor' : 'list') : undefined}>
      <div className="ns-adm-list nx-scroll">
        {canManage ? (
          <div className="ns-reorder-bar">
            {reorder ? (
              <Btn size="sm" icon="check" kind="mode" onClick={() => setReorder(false)}>
                {t('um.drag.reorderDone')}
              </Btn>
            ) : (
              <>
                <Btn
                  size="sm"
                  icon="plus"
                  kind="mode"
                  onClick={() =>
                    !holdForUnsaved() && onSel({ tab: 'channels', id: 'new', newType: 0 })
                  }
                >
                  {t('ns.admin.newChannel')}
                </Btn>
                <Btn size="sm" icon="sort" kind="secondary" onClick={() => setReorder(true)}>
                  {t('um.drag.reorder')}
                </Btn>
              </>
            )}
          </div>
        ) : null}
        {reorder && canManage && gid ? (
          <ChannelReorder guildId={gid} tree={tree} />
        ) : (
          <ul>
            {items.map((c, i) => (
              <li key={c.id} {...dragProps(i)} title={canManage ? t('um.drag.hint') : undefined}>
                <button
                  type="button"
                  className={'ns-adm-item' + (c.type === ChannelType.GUILD_CATEGORY ? ' cat' : '')}
                  aria-current={sel.id === c.id ? 'true' : undefined}
                  onClick={() =>
                    c.id === sel.id || holdForUnsaved() || onSel({ tab: 'channels', id: c.id })
                  }
                >
                  {/* a stage channel is a voice channel too (it had '#') */}
                  <Icon name={channelIcon(c.type)} />
                  <span dir="auto">{c.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="ns-adm-main nx-scroll" ref={main}>
        {compact && sel.id ? <SplitBack label={t('ns.admin.manageChannels')} /> : null}
        {sel.id ? (
          <ChannelEditor
            key={editorKey}
            id={sel.id}
            newType={sel.newType ?? 0}
            parentId={sel.parentId ?? null}
            onDone={(id) => onSel({ tab: 'channels', id })}
          />
        ) : (
          <p className="ns-muted ns-pad">{t('ns.admin.pickChannel')}</p>
        )}
      </div>
    </div>
  );
}

/* ---------------- the layer ---------------- */

type TabDef = {
  id: AdminState['tab'];
  on: boolean;
  icon: IconName;
  label: string;
  group: 'server' | 'mod' | 'owner';
};

/**
 * Server settings: the same full-window layer as User settings (§6.9, P32),
 * with its tabs in the left nav (SERVER, MODERATION, then Ownership), so no
 * tab is ever out of sight (I3).
 */
export function AdminDialog({ state }: { state: AdminState }) {
  const { t } = useT();
  const set = useUi((u) => u.set);
  // the tab it opened on: Server settings as a whole (the server menu) opens
  // on its list on a phone; a deep link (Invites, Channel settings) on its page
  const [opened] = useState(state);
  // the open server's settings (Console v2: many servers)
  const gid = useActiveGuildId();
  const tools = useDataShallow((d) => serverTools(d, gid));
  const voiceMod = useVoiceModChannels(gid).length > 0;
  // custom emoji (2026-11-01): Create or Manage Expressions
  const emojiTab = useData((d) => canSeeEmojiTab(basePermissions(d, gid)));
  const automodOn = useFlag('automod');
  const body = useRef<HTMLDivElement>(null);
  // Escape, the close button and the tabs wait for Save or Reset (unsaved.ts).
  const close = () => holdForUnsaved() || set({ admin: null });
  const tabs: TabDef[] = [
    // the server's name and icon, first as on Discord (fix round 2026-10-28)
    {
      id: 'overview',
      on: tools.overview,
      icon: 'gear',
      label: 'ns.admin.overview',
      group: 'server',
    },
    {
      id: 'channels',
      // a channel moderator opens the voice channels they moderate (2026-10-28)
      on: tools.channels || voiceMod,
      icon: 'hash',
      label: 'ns.admin.manageChannels',
      group: 'server',
    },
    {
      id: 'roles',
      on: tools.roles,
      icon: 'shield',
      label: 'ns.admin.manageRoles',
      group: 'server',
    },
    { id: 'emoji', on: emojiTab, icon: 'smile', label: 'emo.tab', group: 'server' },
    { id: 'invites', on: tools.invites, icon: 'link', label: 'um.admin.invites', group: 'server' },
    // Discord's AutoMod (2026-10-29): Manage Server, while the console has it on
    {
      id: 'automod',
      on: tools.overview && automodOn,
      icon: 'shield-check',
      label: 'am.tab',
      group: 'mod',
    },
    { id: 'audit', on: tools.audit, icon: 'history', label: 'um.admin.audit', group: 'mod' },
    { id: 'bans', on: tools.bans, icon: 'cm-ban', label: 'um.admin.bans', group: 'mod' },
    { id: 'deleted', on: tools.deleted, icon: 'trash', label: 'um.admin.deleted', group: 'mod' },
    { id: 'owner', on: tools.owner, icon: 'crown', label: 'um.admin.owner', group: 'owner' },
  ];
  const allowed = tabs.filter((x) => x.on);
  const tab = allowed.some((x) => x.id === state.tab) ? state.tab : allowed[0]?.id;
  // another tab opens at its top
  useTopOnChange(body, tab ?? '');
  const groupOf = (g: TabDef['group']) =>
    allowed
      .filter((x) => x.group === g)
      .map((x) => ({ id: x.id, icon: x.icon, label: t(x.label), danger: g === 'owner' }));
  const groups = [
    { title: t('adm.group.server'), tabs: groupOf('server') },
    { title: t('adm.group.mod'), tabs: groupOf('mod') },
    { tabs: groupOf('owner') },
  ].filter((g) => g.tabs.length);
  const label = allowed.find((x) => x.id === tab)?.label;
  return (
    <SetLayer
      cls="nx-set-layer ns-adm-layer"
      label={t('ns.admin.title')}
      closeLabel={t('c.close')}
      onClose={close}
      bodyRef={body}
      fill={tab === 'channels' || tab === 'roles'}
      page={tab}
      pageTitle={label ? t(label) : t('ns.admin.title')}
      listFirst={opened.tab === 'overview' && !opened.id}
      nav={
        <SetNav
          label={t('ns.admin.title')}
          listCls="ns-adm-tabs"
          current={tab}
          groups={groups}
          onPick={(id) =>
            id === tab || holdForUnsaved() || set({ admin: { tab: id as AdminState['tab'] } })
          }
        />
      }
    >
      <section
        className="nx-set-sec ns-adm"
        role="tabpanel"
        aria-label={label ? t(label) : t('ns.admin.title')}
        data-tab={tab}
      >
        <h2 className="ns-adm-head">{label ? t(label) : t('ns.admin.title')}</h2>
        {!tab ? (
          <p className="ns-muted">{t('ns.admin.noTools')}</p>
        ) : tab === 'overview' ? (
          <OverviewTab />
        ) : tab === 'channels' ? (
          <ChannelsTab sel={state} onSel={(s) => set({ admin: s })} />
        ) : tab === 'roles' ? (
          <RolesTab sel={state} onSel={(s) => set({ admin: s })} />
        ) : tab === 'emoji' ? (
          <EmojiTab />
        ) : tab === 'invites' ? (
          <InvitesTab all={tools.allInvites} />
        ) : tab === 'automod' ? (
          <AutoModTab />
        ) : tab === 'audit' ? (
          <AuditTab />
        ) : tab === 'bans' ? (
          <BansTab />
        ) : tab === 'deleted' ? (
          <DeletedTab />
        ) : (
          <OwnerTab />
        )}
      </section>
    </SetLayer>
  );
}
